/**
 * The content cache: what this device keeps so it can play without a network
 * (tasks 14.3, 14.6).
 *
 * IndexedDB rather than the Cache Storage API, and application-owned rather
 * than delegated to Workbox runtime caching. Two requirements make that the
 * right call:
 *
 *   - **nothing enters the cache unverified.** A response has to be digested
 *     against the catalog *before* it is stored, which a declarative runtime
 *     caching rule cannot express;
 *   - **eviction prefers never-played packages.** That is a statement about the
 *     player's history, not about HTTP, and the only place that history exists
 *     is here.
 *
 * The service worker still precaches the *shell* — that is Workbox's job and it
 * does it well. This is the content half of the same promise.
 *
 * Everything stored is text, as fetched, not a parsed object. The bytes are
 * what the digest was taken over, so keeping them means a cached package can be
 * re-verified on the way out as cheaply as on the way in.
 */
import { clearStore, get, getAll, openDatabase, put, remove, transact } from '../idb.js';

export const CONTENT_DB_NAME = 'managing-up-content';
export const CONTENT_DB_VERSION = 1;
export const PACKS_STORE = 'packs';
export const DOCUMENTS_STORE = 'documents';

/** How many packages this device keeps before it starts evicting. */
export const DEFAULT_PACK_BUDGET = 12;

/** A cached package, and what the eviction policy needs to know about it. */
export interface CachedPack {
  /** The URL it was fetched from, resolved absolute. The primary key. */
  readonly url: string;
  readonly packId: string;
  readonly packVersion: string;
  /** The bytes, exactly as served. */
  readonly text: string;
  /** The digest they were verified against. */
  readonly sha256: string;
  readonly fetchedAt: string;
  /**
   * When a run was last opened on it, or `null` for a package this device has
   * downloaded but never played. That distinction is the whole eviction policy.
   */
  readonly playedAt: string | null;
}

/** A small content document — the catalog, the profiles, a calibration report. */
export interface CachedDocument {
  readonly key: string;
  readonly text: string;
  readonly fetchedAt: string;
}

export interface ContentCache {
  /** False where IndexedDB is unavailable: reads miss, writes are dropped. */
  readonly available: boolean;
  readDocument(key: string): Promise<CachedDocument | null>;
  writeDocument(key: string, text: string, now: string): Promise<void>;
  readPack(url: string): Promise<CachedPack | null>;
  writePack(pack: CachedPack): Promise<void>;
  /** Stamps a package as played, which is what protects it from eviction. */
  markPlayed(url: string, now: string): Promise<void>;
  listPacks(): Promise<readonly CachedPack[]>;
  deletePack(url: string): Promise<void>;
  /** Evicts down to `budget`, never-played packages first. Returns what went. */
  evict(budget?: number): Promise<readonly string[]>;
  clear(): Promise<void>;
}

/** A cache that holds nothing, for a device with no usable IndexedDB. */
export function nullContentCache(): ContentCache {
  return {
    available: false,
    readDocument: () => Promise.resolve(null),
    writeDocument: () => Promise.resolve(),
    readPack: () => Promise.resolve(null),
    writePack: () => Promise.resolve(),
    markPlayed: () => Promise.resolve(),
    listPacks: () => Promise.resolve([]),
    deletePack: () => Promise.resolve(),
    evict: () => Promise.resolve([]),
    clear: () => Promise.resolve(),
  };
}

/**
 * Which package to give up first.
 *
 * Never-played before played, then oldest before newest within each group. A
 * player who has finished a mission has earned the right to replay it on a
 * plane; a package downloaded ahead of time and never opened has earned
 * nothing, and is the obvious thing to lose (`offline-delivery` spec, "Played
 * packages persist offline").
 *
 * Exported because it is the policy, and the policy is worth testing without a
 * database in front of it.
 */
export function evictionOrder(packs: readonly CachedPack[]): readonly CachedPack[] {
  return [...packs].sort((left, right) => {
    const leftPlayed = left.playedAt !== null;
    const rightPlayed = right.playedAt !== null;
    if (leftPlayed !== rightPlayed) return leftPlayed ? 1 : -1;
    const leftStamp = left.playedAt ?? left.fetchedAt;
    const rightStamp = right.playedAt ?? right.fetchedAt;
    if (leftStamp !== rightStamp) return leftStamp < rightStamp ? -1 : 1;
    // Ties broken by url so eviction is deterministic and a test can assert it.
    return left.url < right.url ? -1 : 1;
  });
}

/**
 * IndexedDB-backed. Every operation degrades to a miss rather than throwing:
 * a device that cannot cache still plays online, and a device whose database
 * was cleared mid-session must not take the run down with it.
 */
export function indexedDbContentCache(
  factory: IDBFactory | null = globalThis.indexedDB ?? null,
  budget = DEFAULT_PACK_BUDGET,
): ContentCache {
  if (factory === null) return nullContentCache();

  const database = openDatabase(factory, {
    name: CONTENT_DB_NAME,
    version: CONTENT_DB_VERSION,
    stores: [
      { name: PACKS_STORE, keyPath: 'url' },
      { name: DOCUMENTS_STORE, keyPath: 'key' },
    ],
  });

  const withStore = async <T>(
    name: string,
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => Promise<T>,
    fallback: T,
  ): Promise<T> => {
    try {
      const db = await database;
      return db === null ? fallback : await transact(db, name, mode, run);
    } catch {
      return fallback;
    }
  };

  const cache: ContentCache = {
    available: true,

    readDocument: (key) =>
      withStore(
        DOCUMENTS_STORE,
        'readonly',
        async (store) => (await get<CachedDocument>(store, key)) ?? null,
        null as CachedDocument | null,
      ),

    writeDocument: (key, text, now) =>
      withStore(
        DOCUMENTS_STORE,
        'readwrite',
        async (store) => {
          await put(store, { key, text, fetchedAt: now });
        },
        undefined,
      ),

    readPack: (url) =>
      withStore(
        PACKS_STORE,
        'readonly',
        async (store) => (await get<CachedPack>(store, url)) ?? null,
        null as CachedPack | null,
      ),

    writePack: (pack) =>
      withStore(
        PACKS_STORE,
        'readwrite',
        async (store) => {
          await put(store, pack);
        },
        undefined,
      ),

    markPlayed: (url, now) =>
      withStore(
        PACKS_STORE,
        'readwrite',
        async (store) => {
          const existing = await get<CachedPack>(store, url);
          if (existing === undefined) return;
          await put(store, { ...existing, playedAt: now });
        },
        undefined,
      ),

    listPacks: () =>
      withStore(PACKS_STORE, 'readonly', (store) => getAll<CachedPack>(store), [] as CachedPack[]),

    deletePack: (url) =>
      withStore(
        PACKS_STORE,
        'readwrite',
        async (store) => {
          await remove(store, url);
        },
        undefined,
      ),

    evict: async (limit = budget) => {
      const packs = await cache.listPacks();
      if (packs.length <= limit) return [];
      const doomed = evictionOrder(packs).slice(0, packs.length - limit);
      for (const pack of doomed) await cache.deletePack(pack.url);
      return doomed.map((pack) => pack.url);
    },

    clear: async () => {
      for (const name of [PACKS_STORE, DOCUMENTS_STORE]) {
        await withStore(
          name,
          'readwrite',
          async (store) => {
            await clearStore(store);
          },
          undefined,
        );
      }
    },
  };

  return cache;
}
